import * as React from "react";
import { createContext, useCallback, useEffect, useState } from "react";
import {
  AddToCartResponse,
  CategoryDictionary,
  FrostDates,
  PlantCategory,
  PlantingLocation,
  PlantType,
  RestProduct,
  SeasonPlantingPlan,
} from "./Types";
import { Product } from "./admin.types";
import dayjs from "dayjs";

type FrostDateCtx = {
  frostDates: null | FrostDates;
  setFrostDates: (frostDates: FrostDates | null) => void;
};
type PlantingLocationCxt = {
  plantingLocation: null | PlantingLocation;
  setPlantingLocation: (location: PlantingLocation | null) => void;

  selectedSeason: SeasonPlantingPlan | null;
  setSelectedSeason: (season: SeasonPlantingPlan) => void;
};
type PlantsCtx = {
  categoryDictionary: CategoryDictionary | null;
  categories: PlantCategory[] | null;
  plantTypes: PlantType[] | null;
};
type SelectedPlantTypeCtx = {
  currency: string;
  selectedPlantType: PlantType | null;
  setSelectedPlantType: (plantType: PlantType | null) => void;
  getProducts: (productIds: string[]) => Promise<{ products: Product[] }>;
  addToCart: (
    id: number,
    variantId: number,
    quantity: number
  ) => Promise<AddToCartResponse | null>;
  getShopifyProducts: (
    handle: string[]
  ) => Promise<(RestProduct | null)[] | null>;
};
const SelectedPlantTypeContext = createContext<SelectedPlantTypeCtx>({
  currency: "$",
  selectedPlantType: null,
  setSelectedPlantType: () => {},
  getProducts: async () => {
    return { products: [] };
  },
  addToCart: async () => {
    return { items: [] };
  },
  getShopifyProducts: async () => null,
});

const FrostDatesContext = createContext<FrostDateCtx>({
  frostDates: null,
  setFrostDates: () => {
    console.error("No setFrostDates function provided");
  },
});
const PlantingLocationContext = createContext<PlantingLocationCxt>({
  plantingLocation: null,
  setPlantingLocation: () => {
    console.error("No setPlantingLocation function provided");
  },
  selectedSeason: null,
  setSelectedSeason: () => {
    console.error("No setSelectedSeason function provided");
  },
});
//@ts-ignore
const plannerConfig: PlannerConfig | undefined = window.plantingPlannerConfig;

export const getPlannerConfig = () => plannerConfig ?? null;
const PlantsContext = createContext<PlantsCtx>({
  categoryDictionary: null,
  categories: null,
  plantTypes: null,
});
type ConfigCtx = {
  config: PlannerConfig | null;
};

type PlannerConfig = {
  storeFront: string;
  bannerImage: string;
  primaryColor: string;
  primaryColorContrastText: string;
  font: {
    family: string;
    style: string;
    weight: number;
    faillback: string;
    ratio: number;
  };
  dev: boolean;
  hosted: boolean;
};
export const plannerUrls = {
  adminApi: {
    // url: "https://planting-planner-admin.azurewebsites.net/",
    url: getPlannerConfig()?.hosted
      ? "https://planting-planner-admin.azurewebsites.net/"
      : "http://localhost:5173/",
  },
  api: () => {
    const plannerConfig = getPlannerConfig();
    if (plannerConfig === null) console.error("No planner config found");
    if (!plannerConfig) return "";
    if (!plannerConfig.hosted) return "";
    return "https://plantingplanner.azurewebsites.net";
  },
  bannerImage: () => getPlannerConfig()?.bannerImage ?? "",
};
const ConfigContext = createContext<ConfigCtx>({ config: getPlannerConfig() });
type PlantInfo = {
  categories: PlantCategory[];
  plantTypes: PlantType[];
  sortedCategies: CategoryDictionary;
};
export type LocalFrostDates = {
  firstDate: {
    day: string;
    probability: number;
  };
  lastDate: {
    day: string;
    probability: number;
  };
  seasonLength: number;
};
type LocalSettings = {
  version: 1;
  plantingLocation: PlantingLocation | null;
  frostDates: LocalFrostDates | null;
  selectedSeason: SeasonPlantingPlan | null;
  selectedPlantType: PlantType | null;
};
type LocalSettingsHydrated = LocalSettings & {
  hydratedFrostDates: FrostDates | null;
};

function getLocalSettings(): LocalSettingsHydrated {
  const currentVersion = 1;
  const localSettings = localStorage.getItem("plantingPlannerSettings");
  const defaultValue: LocalSettingsHydrated = {
    version: 1,
    plantingLocation: null,
    frostDates: null,
    hydratedFrostDates: null,
    selectedSeason: null,
    selectedPlantType: null,
  };
  if (!localSettings) {
    console.log("No local settings found");
    return defaultValue;
  }
  try {
    const settings = JSON.parse(localSettings) as LocalSettings;
    if (settings.version !== currentVersion) {
      console.log("Local settings version mismatch");
      return defaultValue;
    }
    console.log("Local settings found", settings);
    if (settings.frostDates) {
      const hydratedFrostDates: FrostDates = {
        firstDate: {
          day: dayjs(settings.frostDates.firstDate.day),
          probability: settings.frostDates.firstDate.probability,
        },
        lastDate: {
          day: dayjs(settings.frostDates.lastDate.day),
          probability: settings.frostDates.lastDate.probability,
        },
        seasonLength: settings.frostDates.seasonLength,
      };
      return { ...settings, hydratedFrostDates };
    }
    return {...settings, hydratedFrostDates: null};
  } catch (err) {
    console.error("Failed to parse local settings", err);
    return defaultValue;
  }
}
function setLocalFrostDates(frostDates: FrostDates | null) {
  const localSettings = getLocalSettings();
  const newSettings: LocalSettings = {
    ...localSettings,
    frostDates: frostDates
      ? {
          firstDate: {
            day: frostDates.firstDate.day.format("YYYY-MM-DD"),
            probability: frostDates.firstDate.probability,
          },
          lastDate: {
            day: frostDates.lastDate.day.format("YYYY-MM-DD"),
            probability: frostDates.lastDate.probability,
          },
          seasonLength: frostDates.seasonLength,
        }
      : null,
  };
  localStorage.setItem(
    "plantingPlannerSettings",
    JSON.stringify(newSettings)
  );
}
function setLocalPlantingLocation(plantingLocation: PlantingLocation | null) {
  const localSettings = getLocalSettings();
  localStorage.setItem(
    "plantingPlannerSettings",
    JSON.stringify({ ...localSettings, plantingLocation })
  );
}
function setLocalSelectedSeason(selectedSeason: SeasonPlantingPlan | null) {
  const localSettings = getLocalSettings();
  localStorage.setItem(
    "plantingPlannerSettings",
    JSON.stringify({ ...localSettings, selectedSeason })
  );
}
function setLocalSelectedPlantType(selectedPlantType: PlantType | null) {
  const localSettings = getLocalSettings();
  localStorage.setItem(
    "plantingPlannerSettings",
    JSON.stringify({ ...localSettings, selectedPlantType })
  );
}
function AppCtxProvider({ children }: { children: React.ReactNode }) {
  const localSettings = getLocalSettings();
  const [config] = useState<PlannerConfig | null>(getPlannerConfig());
  const [frostDates, setFrostDates] = useState<null | FrostDates>(
    localSettings.hydratedFrostDates
  );
  const [plantingLocation, setPlantingLocation] =
    useState<null | PlantingLocation>(localSettings.plantingLocation);
  const [plantInfo, setPlantInfo] = useState<PlantInfo | null>(null);
  const [selectedPlantType, setSelectedPlantType] = useState<PlantType | null>(
    localSettings.selectedPlantType
  );
  const [selectedSeason, setSelectedSeason] =
    useState<SeasonPlantingPlan | null>(localSettings.selectedSeason);
  const [currency, setCurrency] = useState<string>("$");
  const getCategories = useCallback(async () => {
    try {
      const storeFront = config?.storeFront;
      if (!storeFront) {
        console.error("No store front found in config");
        return;
      }
      const res = await fetch(
        `${plannerUrls.adminApi.url}api/categories?shop=${storeFront}`,
        {
          method: "GET",
        }
      );
      const data = await res.json();
      const catDict = {} as CategoryDictionary;
      data.plantTypes.forEach((row) => {
        const r = row as unknown as PlantType;
        if (!catDict[row.categoryId]) {
          catDict[row.categoryId] = [r];
        } else {
          catDict[row.categoryId].push(r);
        }
      });
      setPlantInfo({ ...data, sortedCategies: catDict });
    } catch (err) {
      console.error(err);
      console.error("Failed to fetch categories");
    }
  }, [config]);
  const getProducts = useCallback(
    async (productIds: string[]) => {
      try {
        const storeFront = config?.storeFront;
        if (!storeFront) {
          console.error("No store front found in config");
          return;
        }
        const res = await fetch(
          `${
            plannerUrls.adminApi.url
          }api/products?shop=${storeFront}&${productIds
            .map((id) => `&id=${id}`)
            .join("")}`,
          {
            method: "GET",
          }
        );
        const data = await res.json();
        console.log(data);
        return data;
      } catch (err) {
        console.error(err);
        console.error("Failed to fetch products");
        return [];
      }
    },
    [config]
  );
  const getCart = useCallback(async () => {
    try {
      /* @ts-ignore */
      const root = window?.Shopify?.routes?.root ?? "/";
      const res = await fetch(`${root}cart.js`, {
        method: "GET",
      });
      const data = await res.json();
      const currency = data.currency === "USD" ? "$" : data.currency;
      setCurrency(currency);
      return data as AddToCartResponse;
    } catch (err) {
      console.error(err);
      console.error("Failed to fetch cart");
      return null;
    }
  }, [config]);
  const addToCart = useCallback(
    async (productId: number, variantId: number, quantity: number) => {
      try {
        console.log("Adding to cart");
        /* @ts-ignore */
        const root = window?.Shopify?.routes?.root ?? "/";

        const body = { items: [{ id: variantId, quantity: quantity }] };
        console.log("adding to cart", body);
        const res = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        console.log(data);
        return data as AddToCartResponse;
      } catch (err) {
        console.error(err);
        console.error("Failed add to cart");
        return null;
      }
    },
    [config]
  );
  const getShopifyProducts = useCallback(
    async (handle: string[]) => {
      try {
        console.log("Fetching products...");
        /* @ts-ignore */
        const root = window?.Shopify?.routes?.root ?? "/";
        console.log("fetching product", handle);
        const products = await Promise.all(
          handle.map(async (h) => {
            try {
              const res = await fetch(`${root}products/${h}.js`);
              if (!res.ok) {
                console.error(`Failed to fetch product ${h}`);
                return null;
              }
              const data = await res.json();
              console.log(data.handle, "fetched");
              return data as RestProduct;
            } catch (err) {
              console.error(err);
              return null;
            }
          })
        );
        console.log(products);
        return products;
      } catch (err) {
        console.error(err);
        console.error("Failed to fetch product");
        return null;
      }
    },
    [config]
  );
  useEffect(() => {
    getCategories();
    getCart();
  }, []);

  return (
    <PlantingLocationContext.Provider
      value={{
        plantingLocation,
        setPlantingLocation: (location) => {
          setLocalPlantingLocation(location);
          setPlantingLocation(location);
        },

        selectedSeason,
        setSelectedSeason: (season) => {
          setLocalSelectedSeason(season);
          setSelectedSeason(season);
        },
      }}
    >
      <FrostDatesContext.Provider
        value={{
          frostDates,
          setFrostDates: (frostDates) => {
            setLocalFrostDates(frostDates);
            setFrostDates(frostDates);
          },
        }}
      >
        <PlantsContext.Provider
          value={{
            categoryDictionary: plantInfo?.sortedCategies ?? null,
            categories: plantInfo?.categories ?? null,
            plantTypes: plantInfo?.plantTypes ?? null,
          }}
        >
          <SelectedPlantTypeContext.Provider
            value={{
              currency,
              selectedPlantType,
              setSelectedPlantType: (plantType) => {
                setLocalSelectedPlantType(plantType);
                setSelectedPlantType(plantType);
              },
              getProducts,
              addToCart,
              getShopifyProducts,
            }}
          >
            <ConfigContext.Provider value={{ config }}>
              {children}
            </ConfigContext.Provider>
          </SelectedPlantTypeContext.Provider>
        </PlantsContext.Provider>
      </FrostDatesContext.Provider>
    </PlantingLocationContext.Provider>
  );
}
export {
  FrostDatesContext,
  PlantingLocationContext,
  PlantsContext,
  SelectedPlantTypeContext,
  AppCtxProvider,
  ConfigContext,
};
